"use client";

import type React from "react";

import { useState } from "react";
import { motion } from "motion/react";
import {
  ArrowLeft,
  Bot,
  BookOpen,
  FileText,
  Search,
  Sparkles,
  CheckCircle,
  ExternalLink,
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { LoginRequired } from "@/components/auth/login-required";
import { toast } from "sonner";

const researchQuery = z.object({
  topic: z.string().min(1),
  depth: z.enum(["basic", "intermediate", "advanced"]).optional(),
});

export default function ResearchAssistantPage() {
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [result, setResult] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<z.infer<typeof researchQuery>>({
    resolver: zodResolver(researchQuery as any),
    defaultValues: {
      topic: "",
      depth: "intermediate",
    },
  });

  const onSubmit = async (data: z.infer<typeof researchQuery>) => {
    setIsSearching(true);
    setHasSearched(false);

    const { topic, depth } = data;

    try {
      const response = await fetch("/api/ai/research", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ topic, depth }),
      });

      const res = await response.json();

      if (!response.ok) {
        throw new Error(res.error || "Failed to get research assistance");
      }

      setResult(res.response);
      setHasSearched(true);
      toast.success("Research information ready!");
    } catch (error: unknown) {
      console.error("Research Assistant Error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setResult(
        `Sorry, I encountered an error: ${errorMessage}. Please try again.`
      );
      setHasSearched(true);

      if (errorMessage.includes("Authentication required")) {
        toast.error("Please log in to use research assistant");
      } else {
        toast.error("Failed to get research assistance");
      }
    } finally {
      setIsSearching(false);
    }
  };

  // const handleTypeFilter = (type: string) => {
  //   setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]))
  // }

  // const filteredResources = resources.filter(
  //   (resource) =>
  //     selectedTypes.includes(resource.type) &&
  //     (relevanceFilter === "all" ||
  //       (relevanceFilter === "high" && resource.relevance >= 90) ||
  //       (relevanceFilter === "medium" && resource.relevance >= 80 && resource.relevance < 90) ||
  //       (relevanceFilter === "low" && resource.relevance < 80)),
  // )

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  // const getResourceTypeIcon = (type: string) => {
  //   switch (type) {
  //     case "article":
  //       return <FileText className="h-4 w-4" />
  //     case "video":
  //       return <BookOpen className="h-4 w-4" />
  //     case "book":
  //       return <BookOpen className="h-4 w-4" />
  //     case "website":
  //       return <ExternalLink className="h-4 w-4" />
  //     default:
  //       return <FileText className="h-4 w-4" />
  //   }
  // }

  // const getResourceTypeColor = (type: string) => {
  //   switch (type) {
  //     case "article":
  //       return "bg-blue-100 text-blue-800"
  //     case "video":
  //       return "bg-red-100 text-red-800"
  //     case "book":
  //       return "bg-green-100 text-green-800"
  //     case "website":
  //       return "bg-purple-100 text-purple-800"
  //     default:
  //       return "bg-gray-100 text-gray-800"
  //   }
  // }

  return (
    <LoginRequired
      message="Sign in to access our AI Research Assistant and get comprehensive research support."
      feature="research"
    >
      <div className="container mx-auto py-10 px-4 md:px-6">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <Link
            href="/ai"
            className="inline-flex items-center text-primary hover:text-primary/80 mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to AI Learning Hub
          </Link>

          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Research Assistant
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Find reliable information and resources for your projects and
            assignments
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <Card className="mb-6">
              {/* <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg">
              <div className="flex items-center">
                <Search className="h-6 w-6 mr-2" />
                <CardTitle>Research Search</CardTitle>
              </div>
              <CardDescription className="text-purple-100">
                Search for reliable academic resources on any topic
              </CardDescription>
            </CardHeader> */}
              <CardContent className="pt-6">
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex space-x-2"
                >
                  <Input
                    {...register("topic")}
                    placeholder="Enter your research topic..."
                    className="flex-grow"
                  />
                  {errors.topic && (
                    <p className="text-destructive">{errors.topic.message}</p>
                  )}
                  <Button type="submit" disabled={isSearching || !isValid}>
                    {isSearching ? "Searching..." : "Search"}
                  </Button>
                </form>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onSubmit({
                        topic: "photosynthesis",
                        depth: "intermediate",
                      })
                    }
                    className="text-xs"
                  >
                    photosynthesis
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onSubmit({
                        topic: "nepal history",
                        depth: "intermediate",
                      })
                    }
                    className="text-xs"
                  >
                    nepal history
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onSubmit({
                        topic: "climate change",
                        depth: "intermediate",
                      })
                    }
                    className="text-xs"
                  >
                    climate change
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onSubmit({ topic: "fractions", depth: "intermediate" })
                    }
                    className="text-xs"
                  >
                    fractions
                  </Button>
                </div>
              </CardContent>
            </Card>

            {isSearching && (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              </div>
            )}

            {hasSearched && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Research Results</h2>
                  <Badge variant="outline">
                    Content generated by AI from Janakalyan Boarding School
                  </Badge>
                </div>

                <div className="prose prose-sm dark:prose-invert max-w-none bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-slate-800 dark:to-slate-700 rounded-lg p-6 border border-emerald-200 dark:border-slate-600">
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children }) => (
                        <h1 className="text-xl font-bold text-emerald-900 dark:text-emerald-100 mb-4">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-lg font-semibold text-emerald-800 dark:text-emerald-200 mb-3">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-md font-medium text-emerald-700 dark:text-emerald-300 mb-2">
                          {children}
                        </h3>
                      ),
                      p: ({ children }) => (
                        <p className="text-slate-700 dark:text-slate-200 mb-3 leading-relaxed">
                          {children}
                        </p>
                      ),
                      ul: ({ children }) => (
                        <ul className="text-slate-700 dark:text-slate-200 ml-4 mb-4 space-y-2">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="text-slate-700 dark:text-slate-200 ml-4 mb-4 space-y-2">
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li className="marker:text-emerald-600 dark:marker:text-emerald-400">
                          {children}
                        </li>
                      ),
                      code: ({ children }) => (
                        <code className="bg-emerald-100 dark:bg-slate-600 text-emerald-800 dark:text-emerald-200 px-2 py-1 rounded font-mono text-sm">
                          {children}
                        </code>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-emerald-400 pl-4 italic text-slate-600 dark:text-slate-300 my-4 bg-emerald-50 dark:bg-slate-600 p-3 rounded">
                          {children}
                        </blockquote>
                      ),
                      strong: ({ children }) => (
                        <strong className="text-emerald-800 dark:text-emerald-200 font-semibold">
                          {children}
                        </strong>
                      ),
                    }}
                  >
                    {result}
                  </Markdown>
                </div>
              </>
            )}

            {!isSearching && !hasSearched && (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-8">
                    <Search className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium mb-2">
                      Start Your Research Journey
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                      Enter a topic in the search box above to find reliable
                      academic resources for your projects and assignments
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                      <div className="bg-secondary/30 p-3 rounded-lg text-center">
                        <FileText className="h-6 w-6 mx-auto text-primary mb-2" />
                        <span className="text-sm font-medium">Articles</span>
                      </div>
                      <div className="bg-accent/30 p-3 rounded-lg text-center">
                        <BookOpen className="h-6 w-6 mx-auto text-primary mb-2" />
                        <span className="text-sm font-medium">Videos</span>
                      </div>
                      <div className="bg-muted/30 p-3 rounded-lg text-center">
                        <BookOpen className="h-6 w-6 mx-auto text-primary mb-2" />
                        <span className="text-sm font-medium">Books</span>
                      </div>
                      <div className="bg-secondary/50 p-3 rounded-lg text-center">
                        <ExternalLink className="h-6 w-6 mx-auto text-primary mb-2" />
                        <span className="text-sm font-medium">Websites</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="space-y-6"
          >
            {/* {hasSearched && (
            <Card>
              <CardHeader>
                <div className="flex items-center">
                  <Filter className="h-5 w-5 mr-2" />
                  <CardTitle>Filter Results</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-medium mb-2">Resource Type</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Checkbox
                        id="type-article"
                        checked={selectedTypes.includes("article")}
                        onCheckedChange={() => handleTypeFilter("article")}
                      />
                      <Label htmlFor="type-article" className="ml-2">
                        Articles
                      </Label>
                    </div>
                    <div className="flex items-center">
                      <Checkbox
                        id="type-video"
                        checked={selectedTypes.includes("video")}
                        onCheckedChange={() => handleTypeFilter("video")}
                      />
                      <Label htmlFor="type-video" className="ml-2">
                        Videos
                      </Label>
                    </div>
                    <div className="flex items-center">
                      <Checkbox
                        id="type-book"
                        checked={selectedTypes.includes("book")}
                        onCheckedChange={() => handleTypeFilter("book")}
                      />
                      <Label htmlFor="type-book" className="ml-2">
                        Books
                      </Label>
                    </div>
                    <div className="flex items-center">
                      <Checkbox
                        id="type-website"
                        checked={selectedTypes.includes("website")}
                        onCheckedChange={() => handleTypeFilter("website")}
                      />
                      <Label htmlFor="type-website" className="ml-2">
                        Websites
                      </Label>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-2">Relevance</h4>
                  <RadioGroup value={relevanceFilter} onValueChange={setRelevanceFilter}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id="relevance-all" />
                      <Label htmlFor="relevance-all">All</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="high" id="relevance-high" />
                      <Label htmlFor="relevance-high">High (90%+)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="medium" id="relevance-medium" />
                      <Label htmlFor="relevance-medium">Medium (80-89%)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="low" id="relevance-low" />
                      <Label htmlFor="relevance-low">Low (Below 80%)</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setSelectedTypes(["article", "video", "book", "website"])
                    setRelevanceFilter("all")
                  }}
                >
                  Reset Filters
                </Button>
              </CardFooter>
            </Card>
          )} */}

            <Card>
              <CardHeader>
                <CardTitle>Research Tips</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <p className="text-sm">
                    Use specific keywords for better results
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <p className="text-sm">
                    Filter by resource type based on your learning style
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <p className="text-sm">
                    Check multiple sources to verify information
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <p className="text-sm">
                    Take notes while reviewing resources
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <p className="text-sm">Always cite your sources properly</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Explore Other AI Tools</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Link href="/ai/chat" className="block">
                  <div className="flex items-center p-2 hover:bg-muted rounded-md transition-colors">
                    <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center mr-3">
                      <Bot className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">AI Chat Assistant</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Get personalized learning support
                      </p>
                    </div>
                  </div>
                </Link>
                <Link href="/ai/homework-helper" className="block">
                  <div className="flex items-center p-2 hover:bg-muted rounded-md transition-colors">
                    <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center mr-3">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">Homework Helper</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Step-by-step homework guidance
                      </p>
                    </div>
                  </div>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12"
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-center">How to Cite Sources</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="mla">
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="mla">MLA Format</TabsTrigger>
                  <TabsTrigger value="apa">APA Format</TabsTrigger>
                  <TabsTrigger value="chicago">Chicago Format</TabsTrigger>
                </TabsList>
                <TabsContent value="mla" className="space-y-4">
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">MLA Format for Books:</h4>
                    <p className="text-sm">
                      Last Name, First Name. <em>Title of Book</em>. Publisher,
                      Publication Date.
                    </p>
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      Example: Smith, John. <em>Understanding Science</em>.
                      Academic Press, 2023.
                    </div>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">
                      MLA Format for Websites:
                    </h4>
                    <p className="text-sm">
                      Author&apos;s Last name, First name. &quot;Title of Web
                      Page.&quot; <em>Website Name</em>, Publisher, Date
                      published, URL.
                    </p>
                  </div>
                </TabsContent>
                <TabsContent value="apa" className="space-y-4">
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">APA Format for Books:</h4>
                    <p className="text-sm">
                      Last Name, F. M. (Year). <em>Title of book</em>.
                      Publisher.
                    </p>
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      Example: Smith, J. (2023). <em>Understanding Science</em>.
                      Academic Press.
                    </div>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">
                      APA Format for Websites:
                    </h4>
                    <p className="text-sm">
                      Last Name, F. M. (Year, Month Day). Title of page. Site
                      Name. URL
                    </p>
                  </div>
                </TabsContent>
                <TabsContent value="chicago" className="space-y-4">
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">
                      Chicago Format for Books:
                    </h4>
                    <p className="text-sm">
                      Last Name, First Name. <em>Title of Book</em>. Place of
                      publication: Publisher, Year.
                    </p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">
                      Chicago Format for Websites:
                    </h4>
                    <p className="text-sm">
                      Last Name, First Name. &quot;Title of Web Page.&quot; Name
                      of Website. Publishing organization, publication or
                      revision date. Access date. URL.
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </LoginRequired>
  );
}
